/**
 * @Description Fasat Grid Footer Bar component
 * @FileName fasatGridFooterBar.js
 * @Author SAMAPTI TALUKDAR-talusam
 * @CreatedOn 22 March, 2021 04:53:07
 * @IssueID 671
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import './fasatGridFooterBar.scss';
import { useTranslation } from 'react-i18next';
import FasatPagination from '../fasatPagination/fasatPagination';
import { scrollToTop } from '../../util';
import { staticCommonLabelKeys } from '../../moduleConstants';

const stringTrue = 'true';
const filterExpandablePanelHeaderID = 'filterExpandablePanel-header';

const fromTORecord = ({ rowCount, currentPageIndex, pageSize }) => {
  const fromRecord = rowCount > 0 ? (currentPageIndex - 1) * pageSize + 1 : 0;
  const toRecord = rowCount > 0 ? (currentPageIndex - 1) * pageSize + rowCount : 0;
  return { fromRecord, toRecord };
};

const recordSummaryContainer = ({ showingLabel,
  fromRecord,
  hypenLabel,
  toRecord,
  ofLabel,
  totalRowCount,
  activateDataGrid }) => {
  let showSummary;
  if (fromRecord > 0 && activateDataGrid) {
    showSummary = () => (
      <div className="recordSummarySection">
        <span>
          {showingLabel}
            &nbsp;
        </span>
        <span data-testid="from-row-num">
          {fromRecord}
        </span>
        <span>{hypenLabel}</span>
        <span data-testid="to-row-num">
          {toRecord}
        </span>
        <span>
            &nbsp;
          {ofLabel}
            &nbsp;
        </span>
        <span data-testid="total-rows-count">
          {totalRowCount > 0 ? totalRowCount : 0}
          {' '}
        </span>
      </div>
    );
  } else if (fromRecord === 0 || !activateDataGrid) {
    showSummary = () => (
      <div className="recordSummarySection">
        <span>
          {showingLabel}
         &nbsp;
        </span>
        <span data-testid="from-row-num">0</span>
        <span>{hypenLabel}</span>
        <span data-testid="to-row-num">0</span>
        <span>
         &nbsp;
          {ofLabel}
         &nbsp;
        </span>
        <span data-testid="total-rows-count"> 0 </span>
      </div>
    );
  } else {
    showSummary = null;
  }

  return { showSummary };
};

function FasatGridFooterBar(props) {
  const [t] = useTranslation();
  const {
    showingLabel,
    ofLabel,
    hypenLabel,
    currentPageIndex,
    pageSize,
    rowCount,
    totalRowCount,
    onPagination,
    activateDataGrid,
  } = props;

  const { fromRecord, toRecord } = fromTORecord({ rowCount, currentPageIndex, pageSize });
  const { showSummary } = recordSummaryContainer({
    showingLabel,
    fromRecord,
    hypenLabel,
    toRecord,
    ofLabel,
    totalRowCount,
    activateDataGrid,
  });

  const onPageChange = useCallback((event) => {
    if (onPagination) {
      onPagination(event);
      if (document.getElementById(filterExpandablePanelHeaderID)
      && document.getElementById(filterExpandablePanelHeaderID).getAttribute('aria-expanded') === stringTrue) {
        document.getElementById(filterExpandablePanelHeaderID).click();
      }
      // Keep table header in view when paginating
      scrollToTop();
    }
  }, [onPagination]);

  return (
    <div className="footerBarWrapper" data-testid="FasatGridFooterBarComponent">
      {showSummary()}
      <FasatPagination
        {...props}
        onPageChange={onPageChange}
        activateDataGrid={activateDataGrid}
        previousAltText={t(staticCommonLabelKeys.ARIA_COMMON_PAGINATION_BUTTON_PREVIOUS)}
        nextAltText={t(staticCommonLabelKeys.ARIA_COMMON_PAGINATION_BUTTON_NEXT)}
        currentItemAriaLabel={t(staticCommonLabelKeys.ARIA_COMMON_PAGINATION_INPUT_LABEL)}
      />
    </div>
  );
}

FasatGridFooterBar.propTypes = {
  showingLabel: PropTypes.string,
  ofLabel: PropTypes.string,
  hypenLabel: PropTypes.string,
  activateDataGrid: PropTypes.bool,
  currentPageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  rowCount: PropTypes.string,
  onPagination: PropTypes.string,
  totalRowCount: PropTypes.number,
};

FasatGridFooterBar.defaultProps = {
  showingLabel: 'Showing',
  ofLabel: 'of',
  hypenLabel: '-',
  activateDataGrid: false,
  currentPageIndex: 0,
  pageSize: 0,
  totalRowCount: 0,
  rowCount: '0',
  onPagination: '0',
};

export default FasatGridFooterBar;
